/**
 * Einführung beim ersten Anmelden.
 *
 * Sieben Karten, die erklären, wie die Anwendung gedacht ist. Bewusst kurz
 * und ohne Werbetexte: wer sie liest, will arbeiten und nicht bewundern.
 *
 * Sie erscheint einmal je Benutzer und Gerät und lässt sich jederzeit erneut
 * öffnen. Der Merker steckt in localStorage und trägt die Benutzerkennung im
 * Schlüssel – auf einem geteilten Diensthandy soll der Zweite die Einführung
 * ebenfalls bekommen und nicht in eine ihm fremde Oberfläche fallen.
 *
 * Warum überhaupt: die zentrale Regel dieser Anwendung – dass "o.B." eine
 * Aussage ist und ein unbeurteilter Punkt keine – versteht niemand von
 * selbst. Sie steht hier auf einer eigenen Karte, weil ein Bericht, der sie
 * verletzt, im Schadensfall ein Problem wäre.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Blatt } from './Blatt'
import { darfVerwalten, useKonto } from '../konto/KontoKontext'
import {
  useBeispiel,
  useBeispielAnlegen,
  useBeispielEntfernen,
} from '../api/kundenakte'

const SCHLUESSEL = 'spuer_einfuehrung_gesehen'

function gesehen(benutzerId: string): boolean {
  try {
    return localStorage.getItem(`${SCHLUESSEL}:${benutzerId}`) === 'ja'
  } catch {
    // Privates Fenster oder gesperrter Speicher: dann eben jedes Mal zeigen.
    // Ein Fehler beim Lesen darf die Anwendung nicht aufhalten.
    return false
  }
}

function merken(benutzerId: string): void {
  try {
    localStorage.setItem(`${SCHLUESSEL}:${benutzerId}`, 'ja')
  } catch {
    // Nicht schlimm – die Einführung kommt dann beim nächsten Mal wieder.
  }
}

type Karte = {
  titel: string
  inhalt: ReactNode
  /** Die Schlussfrage nach dem Beispielkunden: dort heißt „Weiter" anders. */
  abschlussfrage?: boolean
}

export function Einfuehrung({
  erzwungen = false,
  schliessen,
}: {
  /** Aus dem Menü heraus geöffnet, unabhängig vom Merker. */
  erzwungen?: boolean
  schliessen?: () => void
}) {
  const { konto } = useKonto()
  const [offen, setzeOffen] = useState(false)
  const [karte, setzeKarte] = useState(0)
  const beispiel = useBeispiel()
  const anlegen = useBeispielAnlegen()
  const entfernen = useBeispielEntfernen()

  useEffect(() => {
    if (erzwungen) {
      setzeOffen(true)
      setzeKarte(0)
      return
    }
    if (konto && !gesehen(konto.id)) setzeOffen(true)
  }, [konto, erzwungen])

  if (!offen || !konto) return null

  const verwaltet = darfVerwalten(konto)
  const karten = kartenFuer(verwaltet, {
    vorhanden: beispiel.data?.vorhanden ?? false,
    kundeId: beispiel.data?.kunde_id ?? null,
    anlegen: () => anlegen.mutate(),
    laeuftAnlegen: anlegen.isPending,
    schliessen: () => beenden(),
    entfernen: () => entfernen.mutate(undefined, { onSuccess: () => beenden() }),
    laeuftEntfernen: entfernen.isPending,
  })
  const letzte = karte === karten.length - 1

  function beenden() {
    if (konto) merken(konto.id)
    setzeOffen(false)
    schliessen?.()
  }

  return (
    <Blatt
      titel={karten[karte].titel}
      untertitel={`${karte + 1} von ${karten.length}`}
      schliessen={beenden}
      breit
      fuss={
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="knopf-still px-3"
            onClick={() => setzeKarte((k) => Math.max(0, k - 1))}
            disabled={karte === 0}
          >
            Zurück
          </button>

          {/* Punkte statt Zahlen: man sieht auf einen Blick, wie viel noch
              kommt, ohne zu rechnen. */}
          <div aria-hidden className="flex flex-1 justify-center gap-1.5">
            {karten.map((_, i) => (
              <span
                key={i}
                className={`h-2 w-2 rounded-full ${
                  i === karte ? 'bg-akzent-500' : 'bg-slate-300'
                }`}
              />
            ))}
          </div>

          <button
            type="button"
            className="knopf-haupt px-4"
            onClick={() => (letzte ? beenden() : setzeKarte((k) => k + 1))}
          >
            {letzte ? (karten[karte].abschlussfrage ? 'Behalten' : 'Los geht’s') : 'Weiter'}
          </button>
        </div>
      }
    >
      <div className="space-y-3 text-lg">{karten[karte].inhalt}</div>

      {!letzte && (
        <button
          type="button"
          className="mt-6 text-base text-slate-600 underline"
          onClick={beenden}
        >
          Überspringen
        </button>
      )}
    </Blatt>
  )
}

/** Was die Karten über den Beispielkunden wissen und mit ihm tun dürfen. */
type Beispielsteuerung = {
  vorhanden: boolean
  kundeId: string | null
  anlegen: () => void
  laeuftAnlegen: boolean
  entfernen: () => void
  laeuftEntfernen: boolean
  schliessen: () => void
}

function kartenFuer(verwaltet: boolean, beispiel: Beispielsteuerung): Karte[] {
  const karten: Karte[] = [
    {
      titel: 'Willkommen im Prüfmanager',
      inhalt: (
        <>
          <p>
            Diese Anwendung begleitet eine Spielplatzprüfung von der Terminanfrage
            bis zum fertigen Bericht — nach DIN EN 1176/1177, DIN 18034 und
            DGUV 202-022.
          </p>
          <p>
            Sie ersetzt das Word-Protokoll: derselbe Aufbau, dieselben Kriterien,
            aber der Bericht entsteht am Ende von selbst.
          </p>
          <p className="text-base text-slate-600">
            Ein paar kurze Karten. Sie können jederzeit abbrechen und die
            Einführung später über das Menü erneut öffnen.
          </p>
        </>
      ),
    },
    {
      titel: 'Zum Mitschauen: ein Beispielkunde',
      inhalt: (
        <>
          <p>
            Erklären lässt sich vieles. Ansehen ist einfacher. Der
            Beispielkunde ist eine vollständige Akte: zwei Spielplätze mit
            sechs Geräten, eine abgeschlossene Hauptuntersuchung mit einem
            gesperrten Gerät, ein Vertrag, ein Telefonvermerk und ein
            Angebot im Entwurf.
          </p>

          {beispiel.vorhanden ? (
            <>
              <p className="rounded-feld border-2 border-stufe-ob bg-stufe-ob-grund p-3 font-semibold text-stufe-ob">
                Der Beispielkunde ist angelegt.
              </p>
              <p>
                Sie finden ihn unter <span className="font-semibold">Kunden</span> als
                „Gemeinde Musterhausen (Beispiel)". Am Ende dieser Einführung frage
                ich, ob er bleiben soll.
              </p>
              {beispiel.kundeId && (
                <p>
                  <Link
                    to={`/kunden/${beispiel.kundeId}`}
                    onClick={beispiel.schliessen}
                    className="text-akzent-700 underline"
                  >
                    Jetzt ansehen
                  </Link>{' '}
                  <span className="text-base text-slate-600">
                    — die Einführung schließt sich dabei und lässt sich über das
                    Menü erneut öffnen.
                  </span>
                </p>
              )}
            </>
          ) : verwaltet ? (
            <>
              <p>
                Er zählt in keinem Nummernkreis mit: Ihre erste echte Rechnung
                trägt die Nummer 1 und nicht die 2. Und er lässt sich mit einem
                Griff wieder entfernen, samt allem, was an ihm hängt.
              </p>
              <button
                type="button"
                className="knopf-haupt"
                onClick={beispiel.anlegen}
                disabled={beispiel.laeuftAnlegen}
              >
                {beispiel.laeuftAnlegen ? 'Legt an …' : 'Beispielkunden anlegen'}
              </button>
            </>
          ) : (
            <p className="text-slate-700">
              Anlegen kann ihn die Verwaltung. Fragen Sie im Büro nach, wenn Sie
              zum Üben einen Beispielfall brauchen.
            </p>
          )}
        </>
      ),
    },
    {
      titel: 'Kunde, Spielplatz, Gerät',
      inhalt: (
        <>
          <p>Alles hängt an dieser Reihenfolge:</p>
          <p className="rounded-feld bg-slate-100 p-3 font-semibold">
            Kunde → Spielplatz → Spielgerät
          </p>
          <p>
            Der Gerätebestand gehört zum <span className="font-semibold">Spielplatz</span>,
            nicht zur Prüfung. Ein Gerät steht dort jahrelang und wird von jeder
            Folgeprüfung erneut beurteilt — Sie tragen es also nur einmal ein.
          </p>
          <p>
            Anlegen unter <span className="font-semibold">Kunden</span>, ändern über
            den Knopf „Bearbeiten" auf der jeweiligen Seite.
          </p>
        </>
      ),
    },
    {
      titel: 'Der Prüfbogen',
      inhalt: (
        <>
          <p>
            Eine Prüfung legen Sie auf der Seite eines Spielplatzes an. Daraus
            entsteht der Prüfbogen: erst die 13 Platzkriterien nach DIN 18034, dann
            jedes Gerät.
          </p>
          <p>
            Für viele Gerätetypen gibt es einen{' '}
            <span className="font-semibold">Prüfablauf</span> — eine Schrittfolge mit
            Messungen. Weicht ein Messwert vom Sollbereich ab, schlägt die Anwendung
            eine Mängelstufe vor. Entscheiden tun immer Sie.
          </p>
          <p className="text-base text-slate-600">
            Die Abläufe können Sie selbst ändern: Menü → Prüfabläufe.
          </p>
        </>
      ),
    },
    {
      titel: 'Beurteilt ist nicht dasselbe wie ohne Beanstandung',
      inhalt: (
        <>
          <p>Das ist die wichtigste Regel der Anwendung.</p>
          <p>
            <span className="stufe stufe-ob px-2">o.B.</span> heißt:{' '}
            <span className="font-semibold">geprüft und in Ordnung</span>. Das ist eine
            Aussage, die Sie verantworten.
          </p>
          <p>
            <span className="stufe stufe-offen px-2">–</span> heißt:{' '}
            <span className="font-semibold">noch niemand angesehen</span>. Solche Punkte
            sind gestreift dargestellt und lassen sich nicht übersehen.
          </p>
          <p>
            Eine Prüfung mit offenen Punkten lässt sich nicht abschließen — die
            Anwendung nennt Ihnen dann, welche fehlen. So kann im Bericht nie
            „ohne Beanstandung" für ein Gerät stehen, das keiner geöffnet hat.
          </p>
        </>
      ),
    },
    {
      titel: 'Draußen ohne Netz',
      inhalt: (
        <>
          <p>
            Auf dem Platz gibt es oft keine Verbindung. Die Anwendung arbeitet
            deshalb weiter: Beurteilungen und Fotos werden auf dem Gerät gespeichert
            und gesendet, sobald wieder Netz da ist.
          </p>
          <p>
            Oben rechts sehen Sie den Zustand — <span className="marke bg-stufe-1-grund text-stufe-1">offline</span>{' '}
            und die Anzahl der wartenden Änderungen.
          </p>
          <p className="text-base text-slate-600">
            Wichtig: Melden Sie sich draußen nicht ab. Beim Abmelden werden die
            lokalen Prüfdaten gelöscht — das ist Absicht, damit auf einem geteilten
            Gerät nichts zurückbleibt.
          </p>
        </>
      ),
    },
    {
      titel: 'Bericht und Kundenportal',
      inhalt: (
        <>
          <p>
            Beim Abschluss bekommt die Prüfung eine Berichtsnummer, und das PDF
            entsteht. Danach ist sie unveränderlich — Korrekturen laufen über eine
            Nachprüfung.
          </p>
          <p>
            Der Kunde sieht im <span className="font-semibold">Kundenportal</span> seine
            offenen Mängel und die Berichte. Er kann eine Behebung{' '}
            <span className="font-semibold">melden</span>, aber nicht bestätigen — das
            tun Sie bei der Nachprüfung. Wer meldet, erklärt nichts für erledigt.
          </p>
        </>
      ),
    },
  ]

  if (verwaltet) {
    karten.push({
      titel: 'Verwaltung',
      inhalt: (
        <>
          <p>Über das Menü erreichen Sie die Verwaltung. Dort liegen:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <span className="font-semibold">Benutzer und Einladungen</span> — neue
              Mitarbeiter bekommen einen einmaligen Link und setzen ihr Passwort selbst.
            </li>
            <li>
              <span className="font-semibold">Kundenzugänge</span> — Zugänge zum
              Kundenportal.
            </li>
            <li>
              <span className="font-semibold">Unternehmen</span> — Briefkopf und die
              kaufmännischen Angaben für Rechnungen.
            </li>
            <li>
              <span className="font-semibold">Microsoft 365</span> — Anmeldung und
              Mailversand.
            </li>
          </ul>
          <p className="text-base text-slate-600">
            Die Administration prüft nicht selbst. Wenn Sie sehen möchten, was ein
            Prüfer sieht, übernehmen Sie seine Ansicht — das wird festgehalten.
          </p>
        </>
      ),
    })
  }

  // Die Frage ganz zum Schluss, und nur wenn es etwas zu fragen gibt. Sie
  // steht auf einer eigenen Karte statt in einem Bestätigungsfenster: nach
  // sieben Karten soll die letzte Handlung nicht wie ein Fehlalarm aussehen.
  if (beispiel.vorhanden && verwaltet) {
    karten.push({
      titel: 'Soll der Beispielkunde bleiben?',
      abschlussfrage: true,
      inhalt: (
        <>
          <p>
            Sie haben ihn zu Beginn angelegt. Jetzt ist der gute Zeitpunkt zu
            entscheiden, ob er stehen bleibt.
          </p>

          <div className="karte-akzent">
            <p className="font-semibold">Was für Behalten spricht</p>
            <p className="mt-1 text-base">
              Ein vollständiger Fall zum Ausprobieren — Prüfbogen öffnen, Bericht
              erzeugen, Angebot ansehen —, ohne an echten Daten zu üben.
            </p>
            <p className="mt-3 font-semibold">Was für Entfernen spricht</p>
            <p className="mt-1 text-base">
              In den Listen steht sonst neben Ihren echten Kunden eine
              „Gemeinde Musterhausen". Beim Kundentermin fällt das auf.
            </p>
          </div>

          <p className="text-base text-slate-600">
            Beides ist ohne Folgen: Er hat keine Nummer verbraucht, und
            entfernen lässt er sich jederzeit auch später — in der Verwaltung
            unter <span className="font-semibold">Nummern &amp; Beispiel</span>.
          </p>

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              type="button"
              className="knopf-warnung"
              onClick={beispiel.entfernen}
              disabled={beispiel.laeuftEntfernen}
            >
              {beispiel.laeuftEntfernen ? 'Entfernt …' : 'Jetzt entfernen'}
            </button>
          </div>

          <p className="text-base text-slate-600">
            Zum Behalten genügt „Behalten" unten rechts.
          </p>
        </>
      ),
    })
  }

  return karten
}
